import * as logger from 'loglevel'
import React, { useRef, useEffect, useState, useCallback } from 'react'
import useAppDisplayDomSetup from '../Hooks/useAppDisplayDomSetup'
import UserAuthContainer from '../Containers/UserAuthContainer'
import ActionTimeoutDialog from '../UI/ActionTimeoutDialog'
import AnearHelpDialog from '../UI/AnearHelpDialog'
import {
  publishModalActionTimeout,
  shouldDismissPromptOnReset,
  shouldExitPublishActionTimeout,
} from '../Utils/ActionTimeout'
import { sanitizeAnearHtml } from '../Utils/sanitizeAnearHtml'
import { applyFrameDisplay } from '../Utils/applyAnchorTargets'
import { buildFormActionMessage, markClientActionPayload } from '../Utils/actionPayloadEnvelope'
import { awaitPendingMedia } from '../Utils/pendingMedia'

const DefaultContent = '<p>&nbsp;&nbsp;&nbsp;loading app...</p>'

const ChildEventFrame = ({ anearEvent, anearParticipant }) => {
  const frameContentRef = useRef(null)
  const participantSubscribedRef = useRef(false)
  const subscribedParticipantRef = useRef(null)
  const spectatorSubscribedRef = useRef(false)
  const [html, setHtml] = useState(DefaultContent)
  const [lastId, setLastId] = useState(null)
  const [displayRevision, setDisplayRevision] = useState(0)
  const [helpDialogState, setHelpDialogState] = useState({
    open: false,
    title: '',
    html: '',
    theme: {},
  })
  const [actionTimeoutState, setActionTimeoutState] = useState({
    open: false,
    timeoutId: null,
    prompt: null,
    resetLabel: 'Yes',
    exitLabel: 'No',
    modalTimeoutMs: 0,
    timeoutEl: null,
  })
  const lastIdRef = useRef(null)
  const anearParticipantRef = useRef(anearParticipant)
  const modalPenaltyTimerRef = useRef(null)
  const modalPenaltyContextRef = useRef({ timeoutEl: null, timeoutId: null, modalMs: 0 })
  const actionTimeoutElRef = useRef(null)
  const onNavClickRef = useRef(null)
  const onActionClickRef = useRef(null)
  const onFormSubmitRef = useRef(null)
  // Forms whose staged media is still uploading, so a second submit of the same form is
  // dropped while other forms in the display remain submittable.
  const commitInFlightRef = useRef(new Set())
  const userAuth = UserAuthContainer.useContainer()

  useEffect(() => {
    lastIdRef.current = lastId
  }, [lastId])

  useEffect(() => {
    anearParticipantRef.current = anearParticipant
  }, [anearParticipant])

  useEffect(() => {
    actionTimeoutElRef.current = actionTimeoutState.timeoutEl
  }, [actionTimeoutState.timeoutEl])

  const clearModalPenaltyTimer = useCallback(() => {
    if (modalPenaltyTimerRef.current) {
      clearTimeout(modalPenaltyTimerRef.current)
      modalPenaltyTimerRef.current = null
    }
    modalPenaltyContextRef.current = { timeoutEl: null, timeoutId: null, modalMs: 0 }
  }, [])

  const publishModalPenaltyIfNeeded = useCallback(() => {
    const { timeoutEl, timeoutId, modalMs } = modalPenaltyContextRef.current
    if (!timeoutEl) return
    if (timeoutEl.getAttribute('data-anear-timeout-published') === 'modal_timeout') {
      clearModalPenaltyTimer()
      setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))
      return
    }

    const participant = anearParticipantRef.current
    if (participant) {
      logger.info('[ABR] publishing ACTION_TIMEOUT (modal expiry, child)')
      publishModalActionTimeout({
        timeoutEl,
        publishAction: (msg) => participant.publishAction(msg),
        renderId: lastIdRef.current,
        modalMs: modalMs || null,
        timeoutId: timeoutId || null,
      })
    }

    clearModalPenaltyTimer()
    setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))
  }, [clearModalPenaltyTimer])

  const scheduleModalPenaltyTimer = useCallback((timeoutEl, modalMs, timeoutId) => {
    clearModalPenaltyTimer()
    if (!timeoutEl || !modalMs || modalMs <= 0) return
    modalPenaltyContextRef.current = { timeoutEl, timeoutId, modalMs }
    modalPenaltyTimerRef.current = setTimeout(() => {
      publishModalPenaltyIfNeeded()
    }, modalMs)
  }, [clearModalPenaltyTimer, publishModalPenaltyIfNeeded])

  const disarmTimeoutWrapper = useCallback((timeoutEl) => {
    clearModalPenaltyTimer()
    timeoutEl?.disarmForUserAction?.()
  }, [clearModalPenaltyTimer])

  useEffect(() => () => clearModalPenaltyTimer(), [clearModalPenaltyTimer])

  // Listen for timeout expiry events inside this child frame.
  useEffect(() => {
    const root = frameContentRef.current
    if (!root) return undefined

    const onTimeoutExpired = (e) => {
      const detail = e?.detail || {}
      const timeoutEl = detail.timeoutEl || null
      if (!timeoutEl) return

      if (detail.sendActionTimeout) {
        if (timeoutEl.getAttribute('data-anear-timeout-published') === 'action_timeout') return
        if (anearParticipant) {
          const payload = {
            ACTION_TIMEOUT: {
              stage: 'action_timeout',
              renderId: lastIdRef.current,
              actionMs: detail.actionMs ?? null,
              timeoutId: detail.timeoutId ?? null,
            },
          }
          logger.info('[ABR] publishing ACTION_TIMEOUT (child no prompt)', payload)
          timeoutEl.setAttribute('data-anear-timeout-published', 'action_timeout')
          anearParticipant.publishAction(JSON.stringify(payload))
        }
        return
      }

      setActionTimeoutState({
        open: true,
        timeoutId: detail.timeoutId || null,
        prompt: detail.prompt || 'Do you still want to participate?',
        resetLabel: detail.resetLabel || 'Yes',
        exitLabel: detail.exitLabel ?? null,
        modalTimeoutMs: Number(detail.modalMs) || 0,
        timeoutEl,
      })
      scheduleModalPenaltyTimer(timeoutEl, Number(detail.modalMs) || 0, detail.timeoutId || null)
    }

    root.addEventListener('anear-timeout-expired', onTimeoutExpired)
    return () => root.removeEventListener('anear-timeout-expired', onTimeoutExpired)
  }, [html, anearParticipant, scheduleModalPenaltyTimer])

  // Display handlers - same payload shapes as EventFrame: { content } or { targets }
  const displayMessage = useCallback(message => {
    const data = message.data || {}
    setLastId(message.id)
    applyFrameDisplay(frameContentRef.current, data, {
      onFullHtml: setHtml,
      onRevision: () => setDisplayRevision(r => r + 1),
      logPrefix: '[ABR child]'
    })
  }, [])

  const displayPrivateMsg = useCallback(
    msg => {
      // Keep this quiet; message attribution is handled in UserMessaging logs.
      displayMessage(msg)
    }, [displayMessage]
  )

  const displayParticipantsMsg = useCallback(
    msg => {
      logger.info(`child participants message: ${msg.name}`)
      displayMessage(msg)
    }, [displayMessage]
  )

  const displaySpectatorsMsg = useCallback(
    msg => {
      logger.info(`child spectators message: ${msg.name}`)
      displayMessage(msg)
    }, [displayMessage]
  )

  const setupParticipant = useCallback(async () => {
    if (!anearParticipant || participantSubscribedRef.current) return

    participantSubscribedRef.current = true

    // Initialize messaging to set up channels (actions, private, participants)
    await anearParticipant.initMessaging()

    // For open_house events or events in created state, we need to enter presence
    // to trigger PARTICIPANT_ENTER which will cause the StateMachine to announce/start the event.
    // The channels should be available even in 'created' state for the creator/participants.
    try {
      // Enter presence on the actions channel - this notifies the AEM that a participant has joined
      // The AEM will send PARTICIPANT_ENTER to the AppM, which will trigger announceAndStartIfOpenHouse
      // This works even in 'created' state because the participant is allowed to join (creator can always join)
      const requiresGeoLocation = anearEvent.requiresGeoLocationToParticipate()
      await anearParticipant.setActionPresence(requiresGeoLocation)
      logger.debug(`[ChildEventFrame] Successfully entered presence on actions channel for participant ${anearParticipant.id}`)
    } catch (err) {
      logger.error('[ChildEventFrame] Error entering presence on actions channel:', err)
      // Don't proceed with subscriptions if presence entry failed - channels may not be ready
      return
    }

    // Subscribe to messages - channels should be available now that presence is entered
    // For open_house events, the event will transition to announce/live after PARTICIPANT_ENTER
    try {
    if (!anearParticipant.isHost()) {
      anearParticipant.subscribeEventParticipantsMessages(displayParticipantsMsg)
    }
    anearParticipant.subscribeEventPrivateMessages(displayPrivateMsg)
    subscribedParticipantRef.current = anearParticipant
    } catch (err) {
      logger.warn('[ChildEventFrame] Error subscribing to messages (event may still be transitioning):', err)
      // Subscriptions might fail if event is still transitioning, but presence entry should have triggered it
    }
  }, [anearParticipant, anearEvent, displayPrivateMsg, displayParticipantsMsg])

  const setupSpectator = useCallback(async () => {
    if (anearEvent.spectatorsAllowed() && !spectatorSubscribedRef.current && userAuth.anearUser()) {
      // Wait for event to be in a joinable state (announce or live)
      const maxWaitTime = 3000 // 3 seconds max wait
      const checkInterval = 100 // Check every 100ms
      let waited = 0
      
      while (anearEvent.state === 'created' && waited < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, checkInterval))
        waited += checkInterval
      }

      spectatorSubscribedRef.current = true
      // Subscribe - errors will be logged by UserMessaging but won't crash
      await anearEvent.subscribeSpectatorDisplayMessages(userAuth.anearUser(), displaySpectatorsMsg)
    }
  }, [anearEvent, userAuth, displaySpectatorsMsg])

  useEffect(() => {
    let cancelled = false

    const clearChildFrameSubs = (p) => {
      if (!p) return
      p.unSubscribeEventPrivateMessages()
      p.unSubscribeEventParticipantsMessages()
    }

    const setup = async () => {
      if (participantSubscribedRef.current) {
        clearChildFrameSubs(subscribedParticipantRef.current)
        subscribedParticipantRef.current = null
        participantSubscribedRef.current = false
      }
      if (spectatorSubscribedRef.current) {
        spectatorSubscribedRef.current = false
      }

      if (cancelled) return

      if (anearParticipant) {
        await setupParticipant()
      } else if (anearEvent.spectatorsAllowed()) {
        await setupSpectator()
      }
    }

    setup()

    return () => {
      cancelled = true
      if (participantSubscribedRef.current) {
        clearChildFrameSubs(subscribedParticipantRef.current)
        subscribedParticipantRef.current = null
        participantSubscribedRef.current = false
      }
      if (spectatorSubscribedRef.current) {
        spectatorSubscribedRef.current = false
      }
    }
  }, [anearEvent, anearParticipant, setupParticipant, setupSpectator])

  const onNavClick = useCallback(e => {
    const navEl = e.target.closest('anear-nav')
    if (!navEl) {
      logger.warn("No anear-nav element found on click target")
      return false
    }

    const cmd = navEl.getAttribute('action')
    if (!cmd) {
      logger.warn("anear-nav element has no action attribute")
      return false
    }
    // For child apps, nav actions are limited - just handle basic ones
    logger.info(`Child app nav action: ${cmd}`)
    e.stopPropagation()
    return false
  }, [])

  const onActionClick = useCallback(e => {
    if (!anearParticipant) {
      logger.warn("Cannot perform action: not a participant (viewing as spectator)")
      e.stopPropagation()
      return false
    }

    const actionEl = e.target.closest('anear-action')
    if (!actionEl) {
      logger.warn("No anear-action element found on click target", e.target)
      return false
    }

    const timeoutEl = actionEl.closest('anear-timeout')
    if (timeoutEl && typeof timeoutEl.isActionExpired === 'function' && timeoutEl.isActionExpired()) {
      timeoutEl.requestPrompt?.()
      e.stopPropagation()
      return false
    }

    actionEl.classList.add('anear-action-clicked')

    const rawMsg = actionEl.getAttribute('payload')
    const msg = markClientActionPayload(rawMsg)
    if (!msg) {
      logger.warn("anear-action element has no payload attribute", actionEl)
      return false
    }
    
    disarmTimeoutWrapper(timeoutEl)
    anearParticipant.publishAction(msg)
    e.stopPropagation()
    return false
  }, [anearParticipant, disarmTimeoutWrapper])

  const getActionFormMessage = (form, submitter = null) => {
    const customComponents = form.querySelectorAll('anear-counter, anear-choice-group')
    const customComponentNames = new Set()
    customComponents.forEach(component => {
      const name = component.getAttribute('name')
      if (name) {
        customComponentNames.add(name)
      }
    })

    const inputs = form.querySelectorAll(`input:not([type=submit]), select, textarea`)
    const values = {}
    inputs.forEach(input => {
      const key = input.getAttribute('name') || input.id
      if (!key) return
      if (input.type === 'hidden' && customComponentNames.has(key)) {
        return
      }

      if (input.tagName === 'SELECT') {
        values[key] = [...input.options]
          .filter(opt => opt.selected)
          .map(opt => opt.value)
      } else {
        // For grouped radios/checkboxes, only checked controls should be serialized.
        if (input.type === 'radio' || input.type === 'checkbox') {
          if (!input.checked) return
        }
        values[key] = input.value
      }
    })

    customComponents.forEach(component => {
      const name = component.getAttribute('name')
      if (name && component.value !== undefined) {
        values[name] = String(component.value)
      }
    })

    try {
      if (submitter) {
        const name = submitter.getAttribute?.('name') || submitter.name
        if (name) {
          const value = submitter.getAttribute?.('value') ?? submitter.value ?? '1'
          if (values.submit === undefined) values.submit = name
          if (values.submitValue === undefined && value != null) values.submitValue = String(value)
        }
      }
    } catch (_e) {
      // ignore
    }

    const formAction = form.tagName.toLowerCase() === 'anear-form'
      ? form.getAttribute('action')
      : null
    if (!formAction) {
      logger.warn('getActionFormMessage: form action not found')
      return JSON.stringify({})
    }
    const formToken = form.getAttribute('data-form-token') || form.getAttribute('dataFormToken')
    return buildFormActionMessage(formAction, values, formToken)
  }

  const onFormSubmit = useCallback(async e => {
    e.preventDefault?.()

    if (!anearParticipant) {
      logger.warn("Cannot submit form: not a participant (viewing as spectator)")
      e.stopPropagation()
      return false
    }

    const form = e.currentTarget?.tagName?.toLowerCase() === 'anear-form'
      ? e.currentTarget
      : e.target.closest('anear-form')
    if (!form) {
      logger.warn("No anear-form element found on submit target")
      return false
    }

    // Everything synchronous has to happen before the first await, since propagation and
    // the SubmitEvent will not survive the yield.
    e.stopPropagation()
    const submitter = e.submitter || null

    if (commitInFlightRef.current.has(form)) return false
    commitInFlightRef.current.add(form)
    const timeoutEl = form.closest('anear-timeout')
    let gate
    try {
      gate = await awaitPendingMedia(form, timeoutEl)
    } finally {
      commitInFlightRef.current.delete(form)
    }

    if (!gate.proceed) {
      if (gate.expired) timeoutEl.requestPrompt?.()
      if (gate.error) {
        logger.warn(`Form submit aborted: staged media did not upload: ${gate.error.message || gate.error}`)
      }
      return false
    }

    disarmTimeoutWrapper(timeoutEl)
    anearParticipant.publishAction(getActionFormMessage(form, submitter))
    return false
  }, [anearParticipant, disarmTimeoutWrapper])

  const handleTimeoutReset = useCallback(() => {
    const timeoutEl = actionTimeoutElRef.current
    if (!timeoutEl) return

    if (shouldDismissPromptOnReset(timeoutEl)) {
      timeoutEl.dismissPrompt?.()
      setActionTimeoutState(prev => ({ ...prev, open: false }))
      return
    }

    clearModalPenaltyTimer()
    timeoutEl.resetFromYes?.()
    setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))
  }, [clearModalPenaltyTimer])

  const handleTimeoutExit = useCallback(async () => {
    const { timeoutEl, modalTimeoutMs, timeoutId } = actionTimeoutState

    clearModalPenaltyTimer()
    setActionTimeoutState(prev => ({ ...prev, open: false, timeoutEl: null, timeoutId: null }))

    if (shouldExitPublishActionTimeout(timeoutEl) && anearParticipant) {
      logger.info('[ABR] publishing ACTION_TIMEOUT (exit forfeit, child)')
      publishModalActionTimeout({
        timeoutEl,
        publishAction: (msg) => anearParticipant.publishAction(msg),
        renderId: lastIdRef.current,
        modalMs: modalTimeoutMs || null,
        timeoutId: timeoutId || null,
      })
      return
    }

    try {
      await anearParticipant?.leaveEvent('PARTICIPANT_EXIT')
    } catch (err) {
      logger.error('[ChildEventFrame] ActionTimeout: exit failed', err)
    }
  }, [actionTimeoutState, anearParticipant, clearModalPenaltyTimer])

  useEffect(() => {
    onNavClickRef.current = onNavClick
    onActionClickRef.current = onActionClick
    onFormSubmitRef.current = onFormSubmit
  }, [onNavClick, onActionClick, onFormSubmit])

  // Setup DOM event handlers for web components
  useAppDisplayDomSetup(
    frameContentRef,
    {
      userAuthContainer: null, // Child apps don't need user auth container
      anearEvent,
      lastMessageId: lastId,
      displayRevision,
      onNavClick,
      onActionClick,
      onFormSubmit,
      onPublishAction: (msg) => anearParticipant?.publishAction(msg),
      onQrClick: null, // Child apps don't need QR codes
      onHelpOpen: ({ title, html, theme }) => {
        setHelpDialogState({
          open: true,
          title: title || 'Help',
          html: html || '',
          theme: theme || {},
        })
      },
    }
  )

  // Note: Widget state management (expand/collapse/full) is handled by the child app's
  // own views, CSS, and action handlers. The child app manages its own state transitions
  // via its state machine and actions. This component just renders the child app's display.

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'auto' }}>
      <ActionTimeoutDialog
        open={actionTimeoutState.open}
        anearEvent={anearEvent}
        prompt={actionTimeoutState.prompt}
        resetLabel={actionTimeoutState.resetLabel}
        exitLabel={actionTimeoutState.exitLabel}
        modalTimeoutMs={actionTimeoutState.modalTimeoutMs}
        onReset={handleTimeoutReset}
        onExit={handleTimeoutExit}
      />
      <AnearHelpDialog
        open={helpDialogState.open}
        title={helpDialogState.title}
        html={helpDialogState.html}
        theme={helpDialogState.theme}
        onClose={() =>
          setHelpDialogState({
            open: false,
            title: '',
            html: '',
            theme: {},
          })
        }
      />
      {html === DefaultContent ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}>
          <p>Loading child app...</p>
        </div>
      ) : (
        <div
          id={anearEvent.id}
          ref={frameContentRef}
          dangerouslySetInnerHTML={{ __html: sanitizeAnearHtml(html) }}
          style={{ width: '100%', height: '100%', overflow: 'auto' }}
        />
      )}
    </div>
  )
}

export default ChildEventFrame
